/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */
import { isEmpty, trim } from 'lodash';
import { DOCUMENT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';

import {
    promiseSequence,
    deleteEmptyKeys,
    generateRandomUUID,
} from 'common/utils/Utils';
import * as ApiService from './ApiService';
import * as DocumentService from './DocumentService';

const baseUrl = 'quotes';

/**
 *
 * @param {*} id
 * @param {*} query
 */
export function findById(id, query) {
    return ApiService.get(true, `${baseUrl}/${id}`, query);
}

/**
 * retrieves a quote by custom filter
 * @param {number} id
 * @param {string} filterId
 * @returns {Promise<Object>}
 */
export function fetchQuoteByFilterId(id, filterId) {
    return ApiService.get(true, `${baseUrl}/${id}/filter/${filterId}`);
}

/**
 * @param {Object} query
 */
export function find(query) {
    return ApiService.post(true, `${baseUrl}/search`, query);
}

export function count(query) {
    return ApiService.post(true, `${baseUrl}/search/count`, query);
}

/**
 *
 * @param {*} id
 * @param {*} quote
 */
export function patch(id, quote) {
    return ApiService.patch(true, `${baseUrl}/${id}`, quote);
}

export function initQuote(options) {
    const _options = options || {};
    const { opsJobManager, quoteTypeId } = _options;
    return {
        documentTypeId: DOCUMENT_TYPE.QUOTE,
        quoteTypeId: quoteTypeId || '',
        reasonDetails: '',
        reasonSubType: '',
        priorityId: '',
        tradeTypeId: '',
        tradeSubTypeId: '',
        propertyId: '',
        opsJobManager: opsJobManager || '',
        clipLevel: '',
        documentOrigination: null,
        occupied: false,
        instructions: {
            lockboxCode: '',
            gateCode: '',
            special: '',
        },
        poNumber: '',
        contacts: [
            {
                typeId: '',
                name: '',
                email: '',
                mobile: '',
                mobileCountry: 'US',
                phone: '',
                phoneCountry: 'US',
                notifications: [],
                id: null,
                contactPreferenceId: '',
                notes: '',
                validMobile: false,
                validPhone: false,
                primaryContact: false,
            },
        ],
        schedule: null,
        extReferenceNumber1: '',
        extReferenceNumber2: '',
        extPoNumber: '',
        clientId: null,
        contractorId: null,
        contractorTeamMemberId: 0,
        checkLists: [],
        photoRequirements: [],
    };
}

export function initQuoteFromDiagnostic(
    diagnosticWorkOrder,
    userId,
    projectId
) {
    return {
        documentTypeId: DOCUMENT_TYPE.QUOTE,
        quoteTypeId: '',
        priorityId: diagnosticWorkOrder.priorityId || '',
        tradeTypeId: diagnosticWorkOrder.tradeTypeId || '',
        tradeSubTypeId: diagnosticWorkOrder.tradeSubTypeId || '',
        propertyId: diagnosticWorkOrder.propertyId || '',
        property: diagnosticWorkOrder.property || '',
        opsJobManager: diagnosticWorkOrder.opsJobManager,
        coOwner: diagnosticWorkOrder.coOwner,
        approvalOwnerId: diagnosticWorkOrder.approvalOwnerId,
        clipLevel: diagnosticWorkOrder.clipLevel || '',
        documentOrigination: {
            oid: diagnosticWorkOrder.id,
            documentTypeId: diagnosticWorkOrder.documentTypeId,
        },
        occupied: diagnosticWorkOrder.occupied,
        instructions: diagnosticWorkOrder.instructions
            ? {
                  lockboxCode:
                      diagnosticWorkOrder.instructions.lockboxCode || '',
                  gateCode: diagnosticWorkOrder.instructions.gateCode || '',
                  special: diagnosticWorkOrder.instructions.special || '',
              }
            : {
                  lockboxCode: '',
                  gateCode: '',
                  special: '',
              },
        poNumber: diagnosticWorkOrder.poNumber || '',
        contacts: diagnosticWorkOrder.contacts?.length
            ? diagnosticWorkOrder.contacts.map(contact => ({
                  uuid: generateRandomUUID(),
                  id: contact.id || null,
                  typeId: contact.typeId || '',
                  contactPreferenceId: contact.contactPreferenceId || '',
                  notes: contact.notes || '',
                  name: contact.name || '',
                  email: contact.email || '',
                  mobile: contact.mobile || '',
                  mobileCountry: contact.mobileCountry || 'US',
                  phone: contact.phone || '',
                  phoneCountry: contact.phoneCountry || 'US',
                  validPhone: contact.phone ? true : null,
                  validMobile: contact.mobile ? true : null,
                  primaryContact: contact.primaryContact || false,
                  notifications: contact.notifications || [],
              }))
            : [
                  {
                      typeId: '',
                      name: '',
                      email: '',
                      mobile: '',
                      mobileCountry: 'US',
                      phone: '',
                      phoneCountry: 'US',
                      notifications: [],
                      id: null,
                      contactPreferenceId: '',
                      notes: '',
                      validMobile: false,
                      validPhone: false,
                      primaryContact: false,
                  },
              ],
        schedule: diagnosticWorkOrder.schedule || null,
        extReferenceNumber1: diagnosticWorkOrder.extReferenceNumber1 || '',
        extReferenceNumber2: diagnosticWorkOrder.extReferenceNumber2 || '',
        extPoNumber: diagnosticWorkOrder.extPoNumber || '',
        clientId:
            diagnosticWorkOrder.property && diagnosticWorkOrder.property.client
                ? diagnosticWorkOrder.property.client.id
                : '',
        contractorId: diagnosticWorkOrder.contractorId || '',
        contractorTeamMemberId:
            diagnosticWorkOrder.contractorTeamMemberId || '',
        internalCertificationId:
            diagnosticWorkOrder.internalCertificationId || '',
        reasonSubType: '',
        reasonDetails: '',
        job: { projectId },
    };
}

export function create(quote) {
    return ApiService.post(true, 'Quotes', trimQuote(quote));
}

function trimQuote(quote) {
    return {
        ...quote,
        reasonDetails: trim(quote.reasonDetails),
        extReferenceNumber1: trim(quote.extReferenceNumber1),
        extReferenceNumber2: trim(quote.extReferenceNumber2),
        extPoNumber: trim(quote.extPoNumber),
        contacts: quote.contacts.map(contact => ({
            ...contact,
            name: trim(contact.name),
            email: trim(contact.email),
        })),
        instructions: {
            gateCode: trim(quote.instructions.gateCode),
            lockboxCode: trim(quote.instructions.lockboxCode),
            special: trim(quote.instructions.special),
        },
    };
}

export function bulkCreate(request) {
    return ApiService.post(true, `${baseUrl}/bulkCreate`, request);
}
/**
 * validates admin no diagnostic quote creation form
 * @param {object} quote
 * @param {boolean} deprecated
 * @returns {boolean}
 */
export function adminNoDiagnosticCreationFormIsValid(
    quote,
    deprecated = false
) {
    return isValid(quote, deprecated);
}

/**
 * validates admin with diagnostic quote creation form
 * @param {object} quote
 * @returns {boolean}
 */
export function adminDiagnosticCreationFormIsValid(quote) {
    return isValid(quote);
}

export function submit(id, doneOnBehalfOf, reason) {
    const query = {
        doneOnBehalfOf,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/submit`, {}, query);
}

export function cancel(id, reason, reasonId) {
    const query = {
        reason,
        reasonId,
    };
    return ApiService.post(true, `${baseUrl}/${id}/cancel`, {}, query);
}

/** DEPRECATED
 * synchronously validates a quote object
 * @param {object} quote
 * @returns {boolean}
 */
export function isValid_DEPRECATED(quote) {
    if (!quote) {
        return false;
    }
    const {
        documentTypeId,
        quoteTypeId,
        reasonSubType,
        reasonDetails,
        priorityId,
        tradeTypeId,
        tradeSubTypeId,
        propertyId,
        opsJobManager,
        clientId,
        contacts,
        occupied,
    } = quote;
    if (!documentTypeId || documentTypeId !== DOCUMENT_TYPE.QUOTE) {
        return false;
    }
    if (!quoteTypeId) {
        return false;
    }
    if (!reasonSubType) return false;
    if (reasonDetails.length < 4) return false;
    if (!priorityId) {
        return false;
    }
    if (!tradeTypeId) {
        return false;
    }
    if (!tradeSubTypeId) {
        return false;
    }
    if (!propertyId) {
        return false;
    }
    if (!opsJobManager) {
        return false;
    }
    if (!clientId) {
        return false;
    }
    if (
        contacts.length &&
        !isEmpty(deleteEmptyKeys(contacts[0])) &&
        !DocumentService.isValidContact(contacts[0])
    ) {
        return false;
    }
    if (occupied && !contacts[0]) {
        return false;
    }
    if (occupied && !DocumentService.isValidContact(contacts[0])) {
        return false;
    }
    if (occupied && !contacts[0].email && !contacts[0].mobile) {
        return false;
    }
    if (!contacts[0]) {
        return false;
    }
    if (!contacts[0].email && !contacts[0].mobile) {
        return false;
    }
    if (!DocumentService.isValidContact(contacts[0])) {
        return false;
    }
    return true;
}

/**
 * synchronously validates a quote object
 * @param {object} quote
 * @param {boolean} deprecated
 * @returns {boolean}
 */
export function isValid(quote, deprecated = false) {
    if (deprecated) return isValid_DEPRECATED(quote);
    if (!quote) {
        return false;
    }
    const {
        documentTypeId,
        quoteTypeId,
        reasonSubType,
        reasonDetails,
        priorityId,
        tradeTypeId,
        tradeSubTypeId,
        propertyId,
        opsJobManager,
        clientId,
        contacts,
    } = quote;
    if (!documentTypeId || documentTypeId !== DOCUMENT_TYPE.QUOTE) {
        return false;
    }
    if (!quoteTypeId) {
        return false;
    }
    if (!reasonSubType) return false;
    if (reasonDetails.length < 4) return false;
    if (!priorityId) {
        return false;
    }
    if (!tradeTypeId) {
        return false;
    }
    if (!tradeSubTypeId) {
        return false;
    }
    if (!propertyId) {
        return false;
    }
    if (!opsJobManager) {
        return false;
    }
    if (!clientId) {
        return false;
    }
    if (
        !contacts.length ||
        contacts.some(c => !DocumentService.isValidContact(c))
    ) {
        return false;
    }
    if (!contacts.some(c => c.primaryContact)) {
        return false;
    }
    return true;
}

/**
 * synchronously validates a order quote object
 * @param {object} quote
 * @returns {boolean}
 */
export function isValidOrderQuote(quote) {
    if (!quote) {
        return false;
    }
    const {
        documentTypeId,
        quoteTypeId,
        priorityId,
        tradeTypeId,
        tradeSubTypeId,
        opsJobManager,
        clientId,
        contacts,
        hasFulfillmentMethods,
        fulfillmentMethod,
        propertyRequired,
        propertyId,
    } = quote;
    if (!documentTypeId || documentTypeId !== DOCUMENT_TYPE.QUOTE) {
        return false;
    }
    if (!quoteTypeId) {
        return false;
    }
    if (!priorityId) {
        return false;
    }
    if (!tradeTypeId) {
        return false;
    }
    if (!tradeSubTypeId) {
        return false;
    }
    if (!opsJobManager) {
        return false;
    }
    if (!clientId) {
        return false;
    }
    if (!contacts?.length) {
        return false;
    }
    if (hasFulfillmentMethods && !fulfillmentMethod) {
        return false;
    }
    if (propertyRequired && !propertyId) {
        return false;
    }
    return true;
}

/**
 * Create a new note for the quote
 * @param {*} quoteId
 * @param {*} data
 * @param {*} addToComment
 * @param {*} editable
 */
export function createNote(quoteId, data, addToComment, editable) {
    return ApiService.post(true, `${baseUrl}/${quoteId}/notes`, data, {
        addToComment,
        editable,
    });
}

/**
 * Update a note for the quote
 * @param {*} quoteId
 * @param {*} noteId
 * @param {*} data
 */
export function updateNote(quoteId, noteId, data) {
    return ApiService.put(true, `${baseUrl}/${quoteId}/notes/${noteId}`, data);
}

/**
 * Create a new note for the quote
 * @param {*} quoteId
 * @param {*} data
 */
export function createComment(quoteId, data) {
    return ApiService.post(true, `${baseUrl}/${quoteId}/comments`, data);
}

/**
 * Update a note for the quote
 * @param {*} quoteId
 * @param {*} commentId
 * @param {*} data
 */
export function updateComment(quoteId, commentId, data) {
    return ApiService.put(
        true,
        `${baseUrl}/${quoteId}/comments/${commentId}`,
        data
    );
}

/**
 *
 * @param {number} id
 * @param {string} ownerId
 * @param {string} coOwnerId
 * @param {string} approvalOwnerId
 * @returns {Promise<Object>}
 */
export function transferOwner(id, ownerId, coOwnerId, approvalOwnerId) {
    const query = {
        ownerId,
        ...(coOwnerId && { coOwnerId }),
        ...(approvalOwnerId && { approvalOwnerId }),
    };
    return ApiService.post(true, `${baseUrl}/${id}/transferOwner`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} priorityId
 * @param {string} reasonId
 * @param {string} reasonIdLabel
 * @param {string} reason
 */
export function changePriority(
    id,
    priorityId,
    reasonId,
    reasonIdLabel,
    reason
) {
    const query = {
        priorityId,
        reasonId,
        reasonIdLabel,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/changePriority`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} quoteTypeId
 * @param {string} reasonId
 * @param {string} reasonIdLabel
 * @param {string} reason
 */
export function changeQuoteType(
    id,
    quoteTypeId,
    reasonId,
    reasonIdLabel,
    reason
) {
    const query = {
        quoteTypeId,
        reasonId,
        reasonIdLabel,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/changeQuoteType`, {}, query);
}

/**
 * @param {Number} id
 * @param {Object} requirement
 */
export function createPhoto(id, requirement) {
    return ApiService.post(true, `${baseUrl}/${id}/requirements`, requirement);
}

/**
 * @param {Number} id
 * @param {String} photoId
 */
export function deletePhoto(id, photoId) {
    return ApiService.del(`${baseUrl}/${id}/requirements/${photoId}`);
}

export function editUploadedPhotoRequirement(id, photoId, updateRequirement) {
    return ApiService.put(true, `${baseUrl}/${id}/requirements/${photoId}`, {
        ...updateRequirement,
    });
}

/**
 * create a line items
 * @param {number} id
 * @param {array} lineItems
 * @returns {Promise<Object>}
 */
export function bulkCreateLineItems(id, lineItems) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/bulkCreate`,
        lineItems
    );
}

/**
 * create a line item
 * @param {number} id
 * @param {object} lineItem
 * @returns {Promise<Object>}
 */
export function createLineItem(id, lineItem) {
    return ApiService.post(true, `${baseUrl}/${id}/lineItems`, lineItem);
}

/**
 * update a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} lineItem
 * @returns {Promise<Object>}
 */
export function updateLineItem(id, lineItemId, lineItem) {
    return ApiService.put(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}`,
        lineItem
    );
}

/**
 * adjust a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} adjustment
 * @param {boolean} [finance]
 * @returns {Promise<Object>}
 */
export function adjustFinanceLineItem(id, lineItemId, adjustment, finance) {
    const query = {};
    if (finance) {
        query.finance = finance;
    }
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/adjustFinance`,
        adjustment,
        finance
    );
}

/**
 * adjust a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} adjustment
 * @param {boolean} [finance]
 * @returns {Promise<Object>}
 */
export function adjustPayoutPriceLineItem(id, lineItemId, adjustment, finance) {
    const query = {};
    if (finance) {
        query.finance = finance;
    }
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/adjustPayoutPrice`,
        adjustment,
        finance
    );
}

export function bulkAdjustLineItems(
    quoteId,
    bulkAdjustment,
    adjustmentReason,
    adjustmentReasonNote
) {
    const query = {
        bulkAdjustment,
        adjustmentReason,
        adjustmentReasonNote,
    };
    return ApiService.post(
        true,
        `${baseUrl}/${quoteId}/lineItems/bulkAdjustment`,
        {},
        query
    );
}

/**
 * deletes a line item
 * @param {number} id
 * @param {number} lineItemId
 * @returns {Promise<Object>}
 */
export function deleteLineItem(id, lineItemId) {
    return ApiService.del(`${baseUrl}/${id}/lineItems/${lineItemId}`);
}

/**
 * deletes a line item
 * @param {number} quoteId
 * @param {Array<number>} lineItemIds
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function deleteLineItems(quoteId, lineItemIds, reason) {
    return ApiService.post(true, `${baseUrl}/${quoteId}/lineItems/bulkDelete`, {
        lineItemIds,
        reason,
    });
}

/**
 * unapprove a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} reason
 */
export function unapproveLineItem(id, lineItemId, reason) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/unapprove`,
        {},
        { reason }
    );
}

export function reviewed(id) {
    return ApiService.post(true, `${baseUrl}/${id}/reviewed`, {});
}

export function generatePdf(
    id,
    fileName,
    statementOfWork,
    selectedLineItemIds,
    bundlePricing,
    quotePdfType,
    selectedPhotoIds
) {
    const query = {
        fileName,
        statementOfWork,
        includePhotos: true,
        download: true,
        email: false,
        selectedLineItemIds,
        bundlePricing,
        quotePdfType,
        selectedPhotoIds,
    };

    return ApiService.post(true, `${baseUrl}/${id}/export`, {}, query);
}

export function generateOrderQuotePdf(
    id,
    fileName,
    statementOfWork,
    selectedLineItemIds,
    hidePricing,
    hideBundleItems,
    includeSpecSheets,
    includeWarrantySheets,
    quoteLifeSpan
) {
    const query = {
        fileName,
        statementOfWork,
        selectedLineItemIds,
        hidePricing,
        hideBundleItems,
        includeSpecSheets,
        includeWarrantySheets,
        quoteLifeSpan,
    };
    return ApiService.post(
        true,
        `${baseUrl}/${id}/exportOrderQuote`,
        {},
        query
    );
}

export function requestRevision(
    id,
    reasonId,
    subReasonId,
    triReasonId,
    otherReason
) {
    const query = {
        reasonId,
        ...(subReasonId && { subReasonId }),
        ...(triReasonId && { triReasonId }),
        ...(otherReason && { otherReason }),
    };

    return ApiService.post(true, `${baseUrl}/${id}/requestRevision`, {}, query);
}

export function requestApproval(id, data) {
    return ApiService.post(true, `${baseUrl}/${id}/requestApproval`, { data });
}

export function reject(id, reasonId, subReasonId, otherReason) {
    const query = {
        reasonId,
        ...(subReasonId && { subReasonId }),
        ...(otherReason && { otherReason }),
    };

    return ApiService.post(true, `${baseUrl}/${id}/reject`, {}, query);
}

export function approve(id, homeownerResponsibility, extPoNumber) {
    const body = {
        homeownerResponsibility,
        extPoNumber,
    };
    return ApiService.post(true, `${baseUrl}/${id}/approve`, body);
}

export function approveWithHold(id, homeownerResponsibility, extPoNumber) {
    const body = {
        homeownerResponsibility,
        extPoNumber,
    };
    return ApiService.post(true, `${baseUrl}/${id}/approveWithHold`, body);
}

export function order(id) {
    return ApiService.post(true, `${baseUrl}/${id}/order`, {});
}

export function updateContact(quote, contact, occupied, id) {
    let contactApi = null;

    // either add to or update Quote-contacts table
    if (quote.contacts.length && id) {
        contactApi = ApiService.put(
            true,
            `${baseUrl}/${quote.id}/contacts/${id}`,
            contact
        );
    } else {
        contactApi = ApiService.post(
            true,
            `${baseUrl}/${quote.id}/contacts`,
            contact
        );
    }

    const requests = [
        () => contactApi,
        () => ApiService.patch(true, `${baseUrl}/${quote.id}`, occupied),
    ];
    return promiseSequence(requests);
}

export function deleteContact(quoteId, id) {
    return ApiService.del(`${baseUrl}/${quoteId}/contacts/${id}`);
}

/**
 * maps truck store fulfillment details to order object
 * @param {Object} quote
 * @param {Object} fulfillmentDetails
 * @param {String} fulfillmentDetails.fulfillmentToAddress
 * @param {String} fulfillmentDetails.fulfillmentToCity
 * @param {String} fulfillmentDetails.fulfillmentToRegion
 * @param {String} fulfillmentDetails.fulfillmentToPostalCode
 * @param {String} fulfillmentDetails.fulfillmentToStdCounty
 * @param {String} fulfillmentDetails.fulfillmentToStdCountry
 * @param {String} fulfillmentDetails.fulfillmentTypeId
 * @param {String} fulfillmentDetails.fulfillmentTo
 * @param {Number} fulfillmentDetails.distributionCenterId
 * @returns {Object} quote
 */
export function mapFulfillmentDetailsFromTruck(quote, fulfillmentDetails) {
    return {
        ...quote,
        fulfillmentDetails: {
            ...quote.fulfillmentDetails,
            type: fulfillmentDetails.fulfillmentTypeId,
            typeDisplay:
                fulfillmentDetails.fulfillmentTypeDisplay ||
                fulfillmentDetails.fulfillmentTypeId,
            distributionCenterId: fulfillmentDetails.distributionCenterId,
            deliveryLine: fulfillmentDetails.fulfillmentToAddress,
            city: fulfillmentDetails.fulfillmentToCity,
            region: fulfillmentDetails.fulfillmentToRegion,
            postalCode: fulfillmentDetails.fulfillmentToPostalCode,
            name: fulfillmentDetails.fulfillmentTo,
            country:
                fulfillmentDetails.fulfillmentToCountry ||
                fulfillmentDetails.fulfillmentToStdCountry,
            residentialDelivery: fulfillmentDetails.residentialDelivery,
            fulfillmentMethod: fulfillmentDetails.fulfillmentMethod,
        },
    };
}

/**
 *
 * @param {number} id
 * @param {number} contractorId
 * @param {number} contractorTeamMemberId
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function assign(id, contractorId, contractorTeamMemberId, reason) {
    const query = {
        contractorId,
        contractorTeamMemberId,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/assign`, {}, query);
}

/**
 *
 * @param {number|string} id
 * @param {number} contractorId
 * @param {number} contractorTeamMemberId
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function reassign(id, contractorId, contractorTeamMemberId, reason) {
    const query = {
        contractorId,
        contractorTeamMemberId,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/reassign`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function unassign(id, reason) {
    return ApiService.post(true, `${baseUrl}/${id}/unassign`, {}, { reason });
}

/**
 *
 * @param {Number} id
 * @param {Object} _schedule
 * @param {String} _schedule.startDateTime
 * @param {String} _schedule.type
 * @param {string} [reason]
 * @returns {Promise<Object>}
 */
export function schedule(id, _schedule, reason) {
    const query = {};
    if (reason) {
        query.reason = reason;
    }
    return ApiService.post(true, `${baseUrl}/${id}/schedule`, _schedule, query);
}

export function changeProject(quoteId, projectId) {
    return ApiService.patch(
        true,
        `${baseUrl}/${quoteId}/changeProject`,
        {},
        { projectId }
    );
}

export function clone(quoteId, reason) {
    return ApiService.post(true, `${baseUrl}/${quoteId}/clone`, {
        id: parseInt(quoteId, 10),
        reason,
    });
}

export function reopen(quoteId, reason) {
    return ApiService.post(true, `${baseUrl}/${quoteId}/reopen`, {
        id: parseInt(quoteId, 10),
        reason,
    });
}

export function calculateLineItemCost(id, data) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/calculateLineItemCost`,
        data
    );
}

/**
 * substitute a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} newLineItem
 * @param {boolean} isCustom
 * @returns {Promise}
 */
export function substituteLineItem(id, lineItemId, newLineItem, isCustom) {
    const query = {
        isCustom,
    };
    const body = {
        sku: newLineItem.sku,
    };
    if (isCustom) {
        body.suppliedDescription = newLineItem.suppliedDescription;
    }
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/substitute`,
        body,
        query
    );
}

export async function specifyQuoteIssue(quoteId, data) {
    return ApiService.patch(true, `${baseUrl}/${quoteId}/specifyIssue`, data);
}

export async function solveIssueQuoteIssue(quoteId, issueId, data) {
    return ApiService.patch(true, `${baseUrl}/${quoteId}/solveIssue`, data, {
        issueId,
    });
}

export function fetchQuoteIssueTracking(filter) {
    const query = {
        filter,
    };
    return ApiService.get(true, `quoteIssueTrackings`, query);
}

export function getUnassignReasonTypes() {
    return ApiService.get(true, `${baseUrl}/unassignReasonTypes`);
}

export async function updateWorkers(quoteId, workerIds) {
    return ApiService.post(true, `${baseUrl}/${quoteId}/workers`, workerIds);
}

export async function getReassignTeamMembers(data) {
    return ApiService.get(true, `${baseUrl}/getReassignTeamMembers`, data);
}

/**
 * get document timestamps
 * @param {number} id
 * @returns {Promise<Object>}
 */
export function getDocumentTimestamps(id) {
    return ApiService.get(true, `${baseUrl}/${id}/documentTimestamps`);
}

/**
 * retrieves all quote related requirements
 * @param {number} quoteId
 * @returns {Promise<{clientRequirements: Array, requirements: Array}>}
 */
export function getQuoteRelatedRequirements(quoteId) {
    return ApiService.get(true, `${baseUrl}/${quoteId}/relatedRequirements`);
}

/**
 * retrieves all quote related notes
 * @param {number} jobId
 * @returns {Promise<Array>}
 */
export function getQuoteRelatedNotes(jobId) {
    return ApiService.get(true, `${baseUrl}/job/${jobId}/documentNotes`);
}

/**
 * retrieves all quote related comments
 * @param {number} jobId
 * @returns {Promise<Array>}
 */
export function getQuoteRelatedComments(jobId) {
    return ApiService.get(true, `${baseUrl}/job/${jobId}/documentComments`);
}
